/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {generateHex, Minute, Module, ObjectTS} from "@intuitionrobotics/ts-common";
import {DB_Patient} from "@app/app-shared/patient";
import {ThunderDispatcher, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {Api_CreateManyCalendarEvent, Api_DeleteCalendarEvent, Api_GetAllCalendarEvents} from "@app/app-shared/calendar-event";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {CalendarEvent, CalendarEventType, CreateCalendarEvent, Recurrence, ReminderStatusResponse, ScheduledBy} from "@app/ir-q-app-common/types/calendar_event";
import {FONTS} from "@res/fonts";
import dayjs from "../app/utils/dayjs";
import {Reminder} from "@app/app-shared/reminder";
import {GroupData} from "@app/ir-q-app-common/types/db-contact";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import {Api_GetEventStatuses} from "@app/app-shared/reminder-status";
import {DB_Organization, ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {hasUserOrgRoles} from "@modules/utils";
import {OrgRoleLevel} from "@app/app-shared/permission";
import {ContactsMap} from "../app/pages/ui/messages/ContactsContext";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";

export enum ReminderAction {
    Create,
    Delete
}

export interface OnRemindersActions {
    __onRemindersQueried: (success?: boolean) => void;
}

export const dispatch_onRemindersQueried = new ThunderDispatcher<OnRemindersActions, "__onRemindersQueried">("__onRemindersQueried");

type RFCFrequency = {
    freq: "FREQ=DAILY" | "FREQ=WEEKLY" | "NONE";
    days?: string []; // ("SU" | "MO" | "TU" | "WE" | "TH" | "FR" | "SA" )
}

export class RemindersModule_Class
    extends Module {
    constructor() {
        super("reminder");
    }

    private reminders: Record<DB_Patient["_id"], Reminder[] | undefined> = {};
    private reminderStatuses: Record<DB_Patient["_id"], ReminderStatusResponse> = {};

    public getReminderStatuses(patientId: DB_Patient["_id"]): ReminderStatusResponse | undefined {
        return this.reminderStatuses[patientId];
    }

    /**
     * Get only the reminders with type SUGGESTED_REMINDER for a patient
     * @param patient_id
     * @return list of reminders for patient
     */
    getReminders(patient_id: string): Reminder[] | undefined {
        return this.reminders[patient_id];
    };

    filterOnlyCcReminder = (contacts: ContactsMap, organization?: DB_Organization, reminders?: Reminder[]): Reminder[] | undefined => {
        if (!reminders)
            return;

        const organizationData = RemindersModule.getPatientsOrganizationAndContacts(contacts, organization);
        if (!organizationData)
            return;

        return reminders.filter(event => {
            // we only display medication reminders, set by the patients clinical contact. we exclude reminders generated by snoozing
            return (event.metadata?.reminderType === CalendarEventType.SUGGESTED_MEDICATION_REMINDER) &&
                ((organizationData.contacts.orgContact?._id === event.metadata?.contactId) || (organizationData.contacts.customerServiceContact?._id === event.metadata?.contactId));
        })
    }

    /**
     * Convert a timestamp to a date frequency string following rfc2445
     * @param freq either daily or weekly
     * @param timestamp the unix timestamp of the day
     */
    convertToRfcDates(freq: "daily" | "weekly" | "", timestamp: number): string {
        let rfcString = '';

        switch (freq) {
            case "daily":
                rfcString += "FREQ=DAILY";
                break;
            case "weekly":
                rfcString += "FREQ=WEEKLY;BYDAY="
                // convert timestamp to day MO, TU, ...
                const day = dayjs(timestamp).format("dd").toUpperCase();
                rfcString += day;
                break;
            default:
                // no recurrence string, so we leave it as an empty string
                break;
        }
        return rfcString;
    };

    /**
     * Convert a string of type RFC2445 to a RFCFrequency object
     * @param rfcString
     */
    convertRfcDateToString(rfcString: string): RFCFrequency {
        const freq = rfcString.split(";")[0];
        if (freq === "FREQ=DAILY") {
            return {freq};
        }
        if (freq === "FREQ=WEEKLY") {
            const days = rfcString.split(";")[1].replace("BYDAY=", "").split(",");
            return {freq, days};
        }
        return {freq: "NONE"}
    };

    /**
     * Get object with patients clinic and customer service contact for the organization they belong.
     * We also return if the organization is clinical.
     * @param contacts
     * @param organization
     */
    getPatientsOrganizationAndContacts = (contacts: ContactsMap, organization?: DB_Organization) => {
        if (!organization) {
            return
        }

        const contactIds = organization.groupContactIds;
        // we get the contact ids of the organization and filers the patients contacts based on that id
        // we then sort them to set higher priority for clinic
        const relevantContacts = contactIds.map(cId => contacts[cId])

        // return clinic and customer service contacts
        let orgContact;
        if (organization.type === ORGANIZATION_TYPE.CLINICAL)
            orgContact = relevantContacts.find(c => (c.contactData as GroupData).type === GroupType.CLINIC)
        else if (organization.type === ORGANIZATION_TYPE.EXTERNAL)
            orgContact = relevantContacts.find(c => (c.contactData as GroupData).type === GroupType.EXTERNAL)
        else if (organization.type === ORGANIZATION_TYPE.MULTI_CONTACT) {
            orgContact = relevantContacts.find(c => (c.contactData as GroupData).type === GroupType.CLINIC)
        }

        const customerServiceContact = relevantContacts.find((c) => ((c.contactData as GroupData).type === GroupType.CUSTOMER_SERVICE));

        return {type: organization.type, contacts: {orgContact, customerServiceContact}};
    }


    /**
     * Create a reminder for a patient and notify their units
     * @param contacts - contacts
     * @param organization - the organization of the patient
     * @param name - the reminders name
     * @param timestamps - the timestamps of the reminders to create
     * @param requiresMeal - if the reminder requires the patient to consume a meal
     * @param recurrence - recurrence date following the rfc2445 format
     * @param isCriticalMedication - if the reminder is a critical medication
     * @return the created reminders or void if something fails
     */
    generateReminderBody = (contacts: ContactsMap, organization: DB_Organization, name: string, timestamps: number[], requiresMeal: boolean, recurrence: string, isCriticalMedication: boolean = false): CreateCalendarEvent[] | void => {
        const organizationData = this.getPatientsOrganizationAndContacts(contacts, organization);
        if (!organizationData) {
            ToastModule.toastError(`No organization contacts found for patient.`)
            return;
        }

        // We select the clinic contact if the organization is clinical, else the customer service one
        const contact = organizationData.type === ORGANIZATION_TYPE.INTERNAL ? organizationData.contacts.customerServiceContact : organizationData.contacts.orgContact;
        if (!contact)
            return;

        const contactName = organizationData.type === ORGANIZATION_TYPE.INTERNAL ? `${contact.firstName} ${contact.lastName}` : contact.firstName

        return timestamps.map(t => {
            const timestampDate = new Date(t);
            timestampDate.setSeconds(0, 0);
            const utcOffsetApp = timestampDate.getTimezoneOffset() * Minute;
            const startTime = timestampDate.getTime() - utcOffsetApp;

            const metadata: ObjectTS = {
                contactId: contact._id,
                contactName,
                medsMealRequired: requiresMeal,
                reminderType: CalendarEventType.SUGGESTED_MEDICATION_REMINDER,
                isCriticalMedication
            };

            return {
                subject: name,
                eventType: CalendarEventType.REMINDER,
                startTime,
                recurring: recurrence,
                description: 'reminders',
                metadata
            }
        })

    }


    async createReminder(events: CreateCalendarEvent[], patient: DB_Patient) {
        if (events.length === 0) {
            ToastModule.toastError("No reminders to create")
            return;
        }

        const sharedReminderInstance = generateHex(32);
        events.forEach(event => {
            event.metadata = {
                ...event.metadata,
                sharedReminderInstance
            };
        })

        const eventsIds = await XhrHttpModule
            .createRequest<Api_CreateManyCalendarEvent>(HttpMethod.POST, "create-many-suggested-reminder")
            .setJsonBody(
                {
                    unitId: patient.unitId,
                    patientId: patient._id,
                    events,
                    notifyUnits: true
                })
            .setRelativeUrl("/v1/calendar/events/create-many")
            .setOnError((request, resError) => {
                this.logError(`Failed to create medication reminder`, resError);
                ToastModule.toastError(FONTS.medium_16_white("Failed to create a reminder"));
            })
            .executeSync();

        eventsIds.forEach(id =>
            AnalyticsModule.logCareManagerEvent({
                objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
                entity: AnalyticConsts.Entities.USER,
                activityStatus: AnalyticConsts.ActivityStatus.CREATE,
                objectSubType: AnalyticConsts.ObjectSubTypes.CARE_PLAN,
                activityDetails: AnalyticConsts.ActivityDetails.MEDICATION_REMINDER,
                objectId: id.toString()
            })
        );

    }


    fetchRemindersForPatient = (contacts: ContactsMap, organization: DB_Organization, patient: DB_Patient) => {
        const minDate = dayjs().tz(patient.timezone).subtract(1, "year").startOf('year');
        const maxDate = dayjs().tz(patient.timezone).add(1, "year").endOf('year');
        XhrHttpModule
            .createRequest<Api_GetAllCalendarEvents>(HttpMethod.GET, "get-reminders")
            .setUrlParams({
                patientId: patient._id,
                from: minDate.valueOf().toString(),
                to: maxDate.valueOf().toString(),
                types: CalendarEventType.REMINDER
            })
            .setRelativeUrl("/v1/calendar/events/list-all-events")
            .execute((reminders: CalendarEvent[]) => {
                const organizationData = this.getPatientsOrganizationAndContacts(contacts, organization);
                if (!organizationData)
                    return;
                const utcOffsetApp = new Date().getTimezoneOffset() * Minute;
                reminders.forEach(item => item.startTime += utcOffsetApp);
                // Only show SUGGESTED_REMINDERS generated by CARE_CENTER
                this.reminders[patient._id] = reminders.filter((event) => {
                    return (event.metadata?.reminderType === CalendarEventType.SUGGESTED_MEDICATION_REMINDER || event.metadata?.reminderType === CalendarEventType.MEDICATION_REMINDER)
                        && (event.originalEventId === undefined);
                }) as Reminder[];

                if (!this.reminders[patient._id]?.length)
                    dispatch_onRemindersQueried.dispatchUI();
                else
                    this.fetchReminderStatuses(patient, {from: minDate.valueOf(), to: dayjs().valueOf()});
            })
            .setOnError((e) => {
                this.logError(e);
                this.reminders[patient._id] = []
                dispatch_onRemindersQueried.dispatchUI();
            });
    }


    /**
     * Delete the reminder from the patients calendar
     * @param patient
     * @param reminderId
     */
    deleteReminder = async (patient: DB_Patient, reminderId: number) => {
        return XhrHttpModule
            .createRequest<Api_DeleteCalendarEvent>(HttpMethod.POST, "delete-suggested-reminder")
            .setJsonBody(
                {
                    patientId: patient._id,
                    eventId: reminderId,
                    originator: ScheduledBy.CARE_CENTER
                })
            .setRelativeUrl("/v1/calendar/events/delete")
            .setOnError((request, resError) => {
                this.logError(`Failed to delete medication reminder`, resError);
                ToastModule.toastError(FONTS.medium_16_white("Failed to delete a reminder"));
            })
            .executeSync();
    };

    generateRecurrenceString(recurrence: string, displayDays: boolean = true) {
        const rfcRecurrence = RemindersModule.convertRfcDateToString(recurrence);

        const dayConversion: { [key: string]: string } = {
            MO: "Mon",
            TU: "Tue",
            WE: "Wed",
            TH: "Thu",
            FR: "Fri",
            SA: "Sat",
            SU: "Sun"
        }

        let recString = '';
        if (rfcRecurrence.freq === Recurrence.DAILY) {
            recString = 'Daily ';
        }
        if (rfcRecurrence.freq === Recurrence.WEEKLY) {
            recString = 'Weekly ';
            if (rfcRecurrence.days && displayDays) {
                const threeLetterDays = rfcRecurrence.days.map((val) => {
                    if (!val)
                        return ''

                    return dayConversion[val];
                });
                recString = recString.concat(" on ").concat(...threeLetterDays);
            }
        }

        return recString;
    }

    /**
     * Gets reminder statuses for patient in range of dates
     * @param patient
     * @param dates - use epochMillies to define the range
     */
    fetchReminderStatuses = (patient: DB_Patient, dates: { from: number, to: number }) => {
        XhrHttpModule
            .createRequest<Api_GetEventStatuses>(HttpMethod.GET, "get-reminder-statuses")
            .setRelativeUrl(`/v1/calendar/statuses/get-event-statuses`)
            .setUrlParams({unitId: patient.unitId, from: dates.from.toString(), to: dates.to.toString()})
            .execute((activeStatuses) => {
                this.reminders[patient._id]?.forEach((reminder) => {
                    if (!activeStatuses.hasOwnProperty(reminder.id))
                        return;

                    // we modify the timestamp to the start of the day, so we can align the x axis in the graph
                    activeStatuses[reminder.id].forEach((status, _index, table) => {
                        table[_index].timestamp = dayjs(status.timestamp).tz(patient.timezone).startOf("day").valueOf();
                    })

                    // we remove the duplicate statuses for the same day keeping only the last one. the statuses are
                    // sorted by timestamp
                    let index = activeStatuses[reminder.id].length - 1;
                    while (index > 0) {
                        const curr = activeStatuses[reminder.id][index];
                        const prev = activeStatuses[reminder.id][index - 1];
                        if (prev.timestamp === curr.timestamp) {
                            activeStatuses[reminder.id].splice(index - 1, 1);
                            index -= 1;
                        } else
                            index -= 1;
                    }
                })

                this.reminderStatuses[patient._id] = activeStatuses;
                dispatch_onRemindersQueried.dispatchUI();
            }).setOnError((e) => {
            this.logError(e);
            dispatch_onRemindersQueried.dispatchUI();
        })
    }

    setPatientReminders = (d: { [patientId: string]: Reminder[] }) => {
        this.reminders = d;
        dispatch_onRemindersQueried.dispatchUI();
    }

    assertUserCanEditReminders = (patient: DB_Patient) => hasUserOrgRoles(patient.organizationId, [OrgRoleLevel.REMINDERS]);
}

export const RemindersModule = new RemindersModule_Class();
